import React, { useCallback } from "react";
import {
  Button,
  Card,
  Flex,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Text,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Column } from "@jsq/ds/layouts";
import { Colors } from "@jsq/ds/styles";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { AccountingPayableObjectTypeEnum, CurrencyCode } from "#graphql/supergraphEnums";
import { useCreatePaymentBatchMutation } from "#graphql/supergraphHooks";
import { formatCalendarDate } from "#utils/datetime_utils";
import { clientContext } from "#utils/graphql_utils";
import Money from "../../../Money";
import {
  buildTransactionUrl,
  payableObjectTypeToObjectType,
} from "../../../shared/transactionUrl";
import { addMoney, DEFAULT_PRECISION } from "../../../Wallet/utils";
import { usePaymentBatchDataContext } from "../../Context/context";
import { PaymentBatchWorkflowStepProps } from "../../types";
import { getTransactionMoneyAmounts } from "../utils";
import {
  actionButtonsContainer,
  infoCard,
  infoCardText,
  paymentBatchDetailsSubHeader,
  tableCell,
  tableRow,
} from "./styles_vanilla.css";
import { buildCreatePaymentBatchInput } from "./utils";

const ReviewAndSubmitStep: React.FC<PaymentBatchWorkflowStepProps> = ({
  onBack,
  onContinue,
}: PaymentBatchWorkflowStepProps) => {
  const {
    batchData,
    clientName,
    investmentEntityName,
    batchId,
    batchObjectType,
    paymentDescription,
    scheduledPaymentProcessDate,
    selectedPositions,
    numberOfAvailablePositions,
    makerNote,
  } = usePaymentBatchDataContext();
  const bankAccountId = batchData?.entity.bankAccount?.id ?? null;
  const [createPaymentBatch, { loading: isSubmitting }] = useCreatePaymentBatchMutation({
    context: clientContext(),
  });

  const formattedDate = scheduledPaymentProcessDate?.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const zeroAmount = {
    amount: 0,
    currency: selectedPositions[0]?.netPayment.currency || CurrencyCode.Usd,
    precision: DEFAULT_PRECISION,
  };

  const paymentTotal = selectedPositions.reduce(
    (acc, position) => addMoney(acc, [position.netPayment]),
    [zeroAmount],
  );

  const paymentTotalText = `Payment total (${selectedPositions.length} ${
    selectedPositions.length === 1 ? "position" : "positions"
  })`;
  const paymentTotalElement = <Money money={paymentTotal[0]} rounded={false} />;

  const onSubmit = useCallback(() => {
    try {
      if (!batchId) {
        throw new Error("batchId is not set");
      }
      if (!bankAccountId) {
        throw new Error("Entity has no bank account configured");
      }

      const createPaymentBatchInput = buildCreatePaymentBatchInput({
        selectedPositions,
        paymentDescription,
        batchId,
        batchObjectType,
        scheduledPaymentProcessDate,
        makerNote,
        bankAccountId,
      });

      createPaymentBatch({
        variables: { createPaymentBatchInput },
      })
        .then(() => {
          onContinue();
        })
        .catch((err: Error) => {
          addError(formatError(err));
          onContinue(err);
        });
    } catch (err) {
      addError(formatError(err));
      onContinue(err instanceof Error ? err : new Error(String(err)));
    }
  }, [
    createPaymentBatch,
    batchId,
    batchObjectType,
    selectedPositions,
    paymentDescription,
    scheduledPaymentProcessDate,
    makerNote,
    bankAccountId,
    onContinue,
  ]);

  const transactionType = batchData?.subtitleLabel
    ? `${batchData.label} (${batchData.subtitleLabel})`
    : batchData?.label;
  const remainingToPay = batchData
    ? getTransactionMoneyAmounts(batchData, selectedPositions).remainingToPay
    : null;

  const hostName = batchData?.arena?.jsqHostname ?? null;
  const paymentObjectType = payableObjectTypeToObjectType(
    batchObjectType as AccountingPayableObjectTypeEnum,
  );
  const transactionUrl =
    hostName && batchId && paymentObjectType
      ? buildTransactionUrl(paymentObjectType, batchId, hostName)
      : null;

  return (
    <Column size="md">
      <Card title="Review and submit for review">
        <Flex flexDirection="column" gap={16}>
          <Text variant="h4">Client details</Text>
          <Table condensed>
            <TableBody>
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text variant="secondary-md">Client</Text>
                </TableCell>
                <TableCell>
                  <Text variant="regular-md">{clientName}</Text>
                </TableCell>
              </TableRow>
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text variant="secondary-md">Investment entity</Text>
                </TableCell>
                <TableCell>
                  <Text variant="regular-md">{investmentEntityName}</Text>
                </TableCell>
              </TableRow>
              {batchData && remainingToPay && (
                <>
                  <TableRow className={tableRow}>
                    <TableCell className={tableCell}>
                      <Text variant="secondary-md">Transaction type</Text>
                    </TableCell>
                    <TableCell>
                      <Text variant="regular-md">{transactionType}</Text>
                    </TableCell>
                  </TableRow>
                  <TableRow className={tableRow}>
                    <TableCell className={tableCell}>
                      <Text variant="secondary-md">Transaction description</Text>
                    </TableCell>
                    <TableCell>
                      {transactionUrl && batchData.description ? (
                        <Link
                          href={transactionUrl}
                          align="left"
                          icon="external-link"
                          iconPosition="right"
                          shouldOpenInNewTab
                        >
                          {batchData.description}
                        </Link>
                      ) : (
                        <Text variant="regular-md">{batchData.description ?? "—"}</Text>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow className={tableRow}>
                    <TableCell className={tableCell}>
                      <Text variant="secondary-md">Transaction date</Text>
                    </TableCell>
                    <TableCell>
                      <Text variant="regular-md">
                        {formatCalendarDate(batchData.date) ?? ""}
                      </Text>
                    </TableCell>
                  </TableRow>
                  {batchData.requestedPaymentDate && (
                    <TableRow className={tableRow}>
                      <TableCell className={tableCell}>
                        <Text variant="secondary-md">Requested payment date</Text>
                      </TableCell>
                      <TableCell>
                        <Text variant="regular-md">
                          {formatCalendarDate(batchData.requestedPaymentDate) ?? ""}
                        </Text>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow className={tableRow}>
                    <TableCell className={tableCell}>
                      <Text variant="secondary-md">Remaining to pay</Text>
                    </TableCell>
                    <TableCell>
                      <Text variant="regular-md">
                        <Money money={remainingToPay} rounded={false} />
                      </Text>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
          <Text variant="h4" className={paymentBatchDetailsSubHeader}>
            Payment batch details
          </Text>
          <Table condensed>
            <TableBody>
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text variant="secondary-md">Payment date</Text>
                </TableCell>
                <TableCell>
                  <Text variant="regular-md">{formattedDate}</Text>
                </TableCell>
              </TableRow>
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text variant="secondary-md">Payment description for all recipients</Text>
                </TableCell>
                <TableCell>
                  <Text variant="regular-md">{paymentDescription}</Text>
                </TableCell>
              </TableRow>
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text variant="secondary-md">{paymentTotalText}</Text>
                </TableCell>
                <TableCell>
                  <Text variant="regular-md">{paymentTotalElement}</Text>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {numberOfAvailablePositions > selectedPositions.length && (
            <Card className={infoCard}>
              <Flex alignItems="center">
                <Icon color={Colors.Informative700} type="info-circle" size="medium"></Icon>
                <Text className={infoCardText} variant="secondary-md">
                  {numberOfAvailablePositions - selectedPositions.length} position(s) were
                  available to pay, but excluded
                </Text>
              </Flex>
            </Card>
          )}
          {makerNote && (
            <Table condensed>
              <TableBody>
                <TableRow className={tableRow}>
                  <TableCell className={tableCell}>
                    <Text variant="secondary-md">Notes for reviewer</Text>
                  </TableCell>
                  <TableCell>
                    <Text variant="regular-md">{makerNote}</Text>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}

          <Flex flexDirection="row-reverse" className={actionButtonsContainer} gap={10}>
            <Button
              variant={"primary"}
              onClick={onSubmit}
              isLoading={isSubmitting}
              disabled={isSubmitting || selectedPositions.length === 0}
              data-testid="submit-button"
            >
              Submit for review
            </Button>
            <Button
              disabled={isSubmitting}
              variant={"neutral"}
              onClick={onBack}
              data-testid="back-button"
            >
              Back
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Column>
  );
};

export default ReviewAndSubmitStep;
